import {
  ActionIcon,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Pagination,
  Stack,
  Table,
} from '@mantine/core';
import { useField } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import { FixAppHeight } from 'components/FixAppHeight';
import { PageTitle } from 'components/PageTitle';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useState } from 'react';
import { isDefined, isNullish } from 'remeda';
import { fixUtf8StringEncoding } from 'shared/utils';
import { useGetConstants } from 'shared/utils/selectors';

import {
  useAttemptToMatchColumns,
  useLoadCsvFile,
  useMandatoryFieldValidation,
  useMatchedColumns,
  useSubmitCsvData,
} from '../hooks';
import type { MatchedColumns } from '../types.ts';
import { computeFields, computeShouldShowForceBalanceUpdateCheckbox } from '../utils';

import { ColumnMatchingRow } from './ColumnMatchingRow';

const ROWS_PER_PAGE = 15;

type CsvImporterProps = {
  isInvoiceMode: boolean;
};

export const CsvImporter = ({ isInvoiceMode }: CsvImporterProps) => {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();
  const { invoice_import_columns, debtor_import_columns } = useGetConstants();

  const forceBalanceUpdateField = useField({ type: 'checkbox', initialValue: false });
  const { matchedColumns, setMatchedColumns } = useMatchedColumns(isInvoiceMode);

  const { inputElement, csvData, setCsvData } = useLoadCsvFile();
  const [activePage, setPage] = useState(1);

  const { submit, isSubmitloading } = useSubmitCsvData();

  const { areMandatoryFieldsMatched, Indicators } = useMandatoryFieldValidation(
    isInvoiceMode,
    matchedColumns,
  );

  const {
    allFields,
    optionalFields,
    unmatchedOptionalFields,
    mandatoryFields,
    unmatchedMandatoryFields,
  } = computeFields(isInvoiceMode ? invoice_import_columns : debtor_import_columns, matchedColumns);

  useAttemptToMatchColumns(csvData, allFields, matchedColumns, setMatchedColumns);

  const shouldShowForceBalanceUpdateCheckbox = computeShouldShowForceBalanceUpdateCheckbox(
    isInvoiceMode,
    matchedColumns,
    company,
  );

  // #region handlers
  const handleRemoveRow = (indexRowToDelete: number) => {
    setCsvData((csvData ?? []).filter((_, index) => index !== indexRowToDelete));
  };

  const handleMatchColumn = (matchedField: MatchedColumns[number], colIndex: number) => {
    setMatchedColumns(prev => {
      const newMatchedColumns = [...prev];
      newMatchedColumns[colIndex] = matchedField;
      return newMatchedColumns;
    });
  };

  const handleSubmit = () => {
    if (!isDefined(csvData)) return;
    submit(isInvoiceMode, matchedColumns, csvData, forceBalanceUpdateField.getValue());
  };
  // #endregion

  if (isNullish(csvData)) {
    return (
      <>
        <PageTitle>
          {isInvoiceMode ? t(i18nKeys.INVOICES_IMPORT) : t(i18nKeys.FORM.CLIENT_IMPORT.TITLE)}
        </PageTitle>
        {inputElement}
      </>
    );
  }

  const paginationIndex = activePage - 1;
  const rowsOnThisPage = csvData
    .slice(1) // Skip the header row
    .slice(paginationIndex * ROWS_PER_PAGE, paginationIndex * ROWS_PER_PAGE + ROWS_PER_PAGE);

  return (
    <Stack h="100%" gap={0}>
      <FixAppHeight />
      <PageTitle>
        {isInvoiceMode ? t(i18nKeys.INVOICES_IMPORT) : t(i18nKeys.FORM.CLIENT_IMPORT.TITLE)}
        {shouldShowForceBalanceUpdateCheckbox && (
          <PageTitle.Actions>
            <Checkbox
              {...forceBalanceUpdateField.getInputProps()}
              label={t(i18nKeys.FORM.INVOICING.FORCE_BALANCE_UPDATE)}
            />
          </PageTitle.Actions>
        )}
      </PageTitle>
      <Card radius="md" mt="md" p={0}>
        <Table.ScrollContainer minWidth="100%">
          <Table style={{ whiteSpace: 'nowrap' }} striped highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr style={{ borderBottom: 'none' }}>
                <Table.Th bg="purple.1" />
                {csvData?.[0]?.map((header: string, index: number) => (
                  <Table.Th bg="purple.1" key={index}>
                    {fixUtf8StringEncoding(header)}
                  </Table.Th>
                ))}
              </Table.Tr>
              <ColumnMatchingRow
                isInvoiceMode={isInvoiceMode}
                csvData={csvData}
                matchedColumns={matchedColumns}
                mandatoryFields={mandatoryFields}
                unmatchedMandatoryFields={unmatchedMandatoryFields}
                optionalFields={optionalFields}
                unmatchedOptionalFields={unmatchedOptionalFields}
                handleMatchColumn={handleMatchColumn}
              />
            </Table.Thead>
            <Table.Tbody>
              {rowsOnThisPage.map((row, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <ActionIcon variant="subtle" c="red" onClick={() => handleRemoveRow(index + 1)}>
                      <IconTrash stroke={1.5} size={18} />
                    </ActionIcon>
                  </Table.Td>
                  {row.map((cell: string, cellIndex: number) => (
                    <Table.Td key={cellIndex}>{fixUtf8StringEncoding(cell)}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      {csvData.length - 1 > ROWS_PER_PAGE && (
        <Center my="md">
          <Pagination
            value={activePage}
            total={Math.ceil((csvData.length - 1) / ROWS_PER_PAGE)}
            onChange={setPage}
            siblings={2}
            boundaries={2}
          />
        </Center>
      )}
      <Center mt="md">
        {Indicators}
        <Box pos="absolute" right={0} style={{ transform: 'translateX(-50%)' }}>
          <Button
            disabled={!areMandatoryFieldsMatched}
            onClick={handleSubmit}
            loading={isSubmitloading}
          >
            {t(i18nKeys.SAVE)}
          </Button>
        </Box>
      </Center>
    </Stack>
  );
};
